import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ProvidedJobPostsResponse } from '../types/ProvidedJobPost';
import { Button } from '@/components/Button';
import { linkifyText } from '@/utils/linkify';
import Message from '@/components/Message';
import Select from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import ProvidedJobPostDeleteConfirmModal from './ProvidedJobPostDeleteConfirmModal';
import DuplicateConfirmDialog from './DuplicateConfirmDialog';
import useUpdateProvidedJobPostSettings from '../api/useUpdateProvidedJobPostSettings';
import useDuplicateProvidedJobPost from '../api/useDuplicateProvidedJobPost';
import useArchiveProvidedJobPost from '../api/useArchiveProvidedJobPost';
import { showSuccessToast, showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import useFetchOrganization from '@/features/agent/organizations/api/useFetchOrganization';

type Props = {
  jobPost: ProvidedJobPostsResponse | undefined;
};

export default function ProvidedJobPostsSettingsCard({ jobPost }: Props) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [memo, setMemo] = useState(jobPost?.memo || '');
  const [isMemoSaving, setIsMemoSaving] = useState(false);
  const navigate = useNavigate();
  const updateSettings = useUpdateProvidedJobPostSettings(jobPost?.id || 0);
  const duplicateJobPost = useDuplicateProvidedJobPost();
  const archiveJobPost = useArchiveProvidedJobPost();
  const { data: organizationData } = useFetchOrganization();

  if (!jobPost || !organizationData) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-center">
          <div className="mb-4 inline-block size-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="text-gray-500">読み込み中...</p>
        </div>
      </div>
    );
  }

  const handleUpdateStrategyChange = async (value: string) => {
    try {
      await updateSettings.mutateAsync({
        update_strategy: value as 'automatic' | 'manual',
      });
      showSuccessToast('更新設定を保存しました');
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : '更新に失敗しました');
    }
  };

  const updateStrategyOptions = [
    { value: 'automatic', label: '自動更新を許可する' },
    { value: 'manual', label: '自動更新を許可しない' },
  ];

  const handleDuplicate = async () => {
    try {
      await duplicateJobPost.mutateAsync({ id: jobPost.id });
      showSuccessToast('求人票を複製しました');
      setIsDuplicateDialogOpen(false);
      navigate({ to: '/agent/provided_job_posts' });
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : '複製に失敗しました');
    }
  };

  const handleMemoSave = async () => {
    setIsMemoSaving(true);
    try {
      await updateSettings.mutateAsync({ memo });
      showSuccessToast('社内メモを保存しました');
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : '保存に失敗しました');
    } finally {
      setIsMemoSaving(false);
    }
  };

  const handleArchive = async () => {
    try {
      await archiveJobPost.mutateAsync({ id: jobPost.id });
      showSuccessToast('求人票をアーカイブしました');
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'アーカイブに失敗しました');
    }
  };

  return (
    <div className="">
      <div className="flex flex-col gap-lg rounded-sm bg-white p-6 shadow-layer1">
        {jobPost.outer_job_post && (
          <div>
            <h2 className="text-heading-xs">自動更新設定</h2>
            {organizationData?.job_post_setting.is_allow_sync ? (
              <>
                <div className="mt-2 rounded bg-blue-50 p-3">
                  <p className="text-xs text-blue-700">
                    組織全体の設定で「自動更新を許可する」が有効です。この求人票も「自動更新を許可する」に設定することで、外部連携先の更新が自動反映されます。
                  </p>
                </div>
                <div className="mt-4 w-[236px]">
                  <Select
                    items={updateStrategyOptions}
                    value={jobPost.update_strategy}
                    onValueChange={handleUpdateStrategyChange}
                    defaultLabel="更新方法を選択"
                    disabled={updateSettings.isPending}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="mt-2 rounded bg-yellow-50 p-3">
                  <p className="text-xs text-yellow-700">
                    組織全体の設定で「自動更新を許可する」が無効です。自動更新を有効にするには、まず組織設定で「自動更新を許可する」を有効にしてください。
                  </p>
                </div>
                <div className="mt-4 w-[236px]">
                  <Select
                    items={updateStrategyOptions}
                    value={jobPost.update_strategy}
                    onValueChange={handleUpdateStrategyChange}
                    defaultLabel="更新方法を選択"
                    disabled={updateSettings.isPending}
                  />
                </div>
              </>
            )}
          </div>
        )}
        <div>
          <h2 className="text-heading-xs">エージェント向け情報</h2>
          <div className="mt-4">
            {jobPost.agent_content_json && jobPost.agent_content_json.length > 0 ? (
              <table className="w-full border-collapse border border-solid border-border">
                <tbody>
                  {jobPost.agent_content_json.map((item, index) => (
                    <tr key={index} className="h-xxl">
                      <th className="border border-solid border-border bg-gray-50 p-md text-heading-xxs">
                        {item.key}
                      </th>
                      <td className="border border-solid border-border p-md text-body-sm text-main">
                        <div className="whitespace-pre-wrap">
                          {linkifyText(item.content, 'text-link underline')}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-gray-500">情報なし</p>
            )}
          </div>
        </div>
        <div>
          <h2 className="text-heading-xs">社内メモ</h2>
          <p className="mt-1 text-xs text-gray-500">
            この求人に関する社内向けのメモを記録できます。求職者には表示されません。
          </p>
          <div className="mt-4">
            <Textarea
              value={memo}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMemo(e.target.value)}
              className="min-h-[120px]"
              disabled={isMemoSaving}
            />
          </div>
          <div className="mt-3 flex justify-end">
            <Button
              type="button"
              color="primary"
              size="sm"
              onClick={handleMemoSave}
              disabled={isMemoSaving}
            >
              {isMemoSaving ? '保存中...' : '保存'}
            </Button>
          </div>
        </div>
        <div>
          <h2 className="text-heading-xs">求人票の操作</h2>
          <div className="mt-4">
            <div className="flex flex-col gap-3">
              <DuplicateConfirmDialog
                title={jobPost.title}
                onConfirm={handleDuplicate}
                isLoading={duplicateJobPost.isPending}
                open={isDuplicateDialogOpen}
                onOpenChange={setIsDuplicateDialogOpen}
              >
                <Button type="button" color="secondary" size="md" className="w-fit">
                  複製する
                </Button>
              </DuplicateConfirmDialog>
            </div>
          </div>
        </div>
        {(jobPost?.is_possible_delete || jobPost?.is_possible_archive) && (
          <div>
            <h2 className="mb-sm text-heading-xs">危険な操作</h2>
            <div className="space-y-4">
              {jobPost?.is_possible_delete && (
                <Message color="error">
                  <div className="flex items-center justify-between">
                    <p className="text-body-sm text-main">
                      {jobPost?.counter_cache.count_of_suggest > 0 ? (
                        <>
                          この求人票は候補者に既に提案しています。提案中の求職者からは見えなくなります。
                          <br />
                          既に求職者の進捗が進んでいるため、完全に削除はされません。後から求人内容を確認することができます。
                        </>
                      ) : (
                        'この求人情報を削除します'
                      )}
                    </p>
                    <Button
                      type="button"
                      color="danger-secondary"
                      size="sm"
                      onClick={() => setIsDeleteModalOpen(true)}
                    >
                      削除
                    </Button>
                  </div>
                </Message>
              )}
              {jobPost?.is_possible_archive && (
                <Message color="error">
                  <div className="flex items-center justify-between">
                    <p className="text-body-sm text-main">この求人票をクローズします</p>
                    <Button
                      type="button"
                      color="danger-secondary"
                      size="sm"
                      onClick={handleArchive}
                      disabled={archiveJobPost.isPending}
                    >
                      {archiveJobPost.isPending ? 'クローズ中...' : 'クローズ'}
                    </Button>
                  </div>
                </Message>
              )}
            </div>
          </div>
        )}
      </div>
      <ProvidedJobPostDeleteConfirmModal
        modal={{
          id: `provided-job-post-delete-modal-${jobPost.id}`,
          isShow: isDeleteModalOpen,
        }}
        close={() => setIsDeleteModalOpen(false)}
        providedJobPostId={jobPost.id}
        jobPostTitle={jobPost.title}
        onSuccess={() => {
          navigate({ to: '/agent/provided_job_posts' });
        }}
      />
    </div>
  );
}
